/**
 * Store shell, Products → Bulk AI (flag `bulk_ai_operations`, estimate row
 * B33, MOT-163). Two tabs, one concern each: "New job" picks a subset and an
 * operation and shows the estimate before anything runs; "Jobs" lists past
 * jobs and, for one job, the before/after table where every row is approved
 * or rejected by hand. Nothing reaches the store's overrides until Apply, and
 * Apply writes only the approved rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutoFixHighOutlined, CheckOutlined, CloseOutlined, PlayArrowOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useUrlTab } from '../hooks/useUrlTab';
import { useOrganizations } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import {
  aiBulkApi,
  AiBulkJob,
  AiBulkMissingField,
  AiBulkOperation,
  AiBulkRow,
  AiBulkRowDecision,
  AiBulkUsage,
} from '../services/aiBulkApi';
import {
  ConfirmDialog,
  DataTableShell,
  EmptyState,
  InlineMessage,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
  TabShell,
  TableSkeleton,
  UsageBar,
} from '../components/ui';
import type { StatusTone } from '../components/ui';
import { tokens } from '../theme/tokens';
import { TrialFeatureTitle } from '../components/trial/TrialFeatureBadge';

type Tab = 'new' | 'jobs';
const TABS: readonly Tab[] = ['new', 'jobs'];
type RowFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'error';
type ProductTypeFilter = '' | 'camera' | 'lens' | 'accessory';

const WRITE_ROLES = new Set(['owner', 'admin']);
const OPERATIONS: AiBulkOperation[] = ['rewrite_title', 'rewrite_description', 'fill_specs', 'classify_tag', 'normalize_name'];
const COPY_OPERATIONS = new Set<AiBulkOperation>(['rewrite_title', 'rewrite_description']);
const MISSING_FIELDS: AiBulkMissingField[] = ['description', 'shortDescription', 'features', 'specifications', 'tags'];

const JOB_TONE: Record<AiBulkJob['status'], StatusTone> = {
  estimated: 'neutral',
  running: 'info',
  previewed: 'caution',
  budget_exhausted: 'problem',
  applied: 'positive',
  failed: 'problem',
};

const DECISION_TONE: Record<AiBulkRowDecision, StatusTone> = { pending: 'caution', approved: 'positive', rejected: 'neutral' };

/** A row value as text: a string as is, tags joined, a spec map one line per field. */
const renderValue = (value: unknown): React.ReactNode => {
  if (value == null || value === '') return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (Array.isArray(value)) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        {value.map((item, i) => (
          <Chip key={`${String(item)}-${i}`} size="small" label={String(item)} />
        ))}
      </Stack>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25 }}>
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <Typography component="dt" variant="body2" sx={{ fontWeight: 600 }}>{k}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>{String(v)}</Typography>
          </React.Fragment>
        ))}
      </Box>
    );
  }
  return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>;
};

const StoreBulkAiPage: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatCurrency, formatDate } = useLocale();
  const queryClient = useQueryClient();
  const { isEnabled, isLoading: flagsLoading } = useOrgFeatureFlags();
  const enabled = isEnabled('bulk_ai_operations');
  const { actingOrganization } = useOrganizations();
  const { isAdmin } = useAuth();
  const canWrite = isAdmin || (actingOrganization ? WRITE_ROLES.has(actingOrganization.role) : false);
  const [tab, setTab] = useUrlTab<Tab>(TABS, 'new');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobId = searchParams.get('job');

  const usageQuery = useQuery<AiBulkUsage>({ queryKey: ['ai-bulk-usage'], queryFn: aiBulkApi.getUsage, enabled });
  const usage = usageQuery.data;
  const planLocked = usageQuery.error instanceof Error && /AI content tools|plan/i.test(usageQuery.error.message);

  const selectJob = (id: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('job', id);
        else params.delete('job');
        params.set('tab', 'jobs');
        return params;
      },
      { replace: true },
    );
  };

  if (!flagsLoading && !enabled) {
    return (
      <PageContainer>
        <EmptyState title={t('bulkAiPage.unavailableTitle')} description={t('bulkAiPage.unavailableBody')} />
      </PageContainer>
    );
  }

  const limitText = usage ? (usage.limit < 0 ? t('bulkAiPage.noCap') : usage.limit.toLocaleString()) : '';

  return (
    <PageContainer>
      <PageHeader title={<TrialFeatureTitle title={t('bulkAiPage.title')} feature="aiContentTools" />} description={t('bulkAiPage.description')} />

      {planLocked && (
        <InlineMessage severity="warning">
          {t('bulkAiPage.planLocked')}{' '}
          <Button size="small" href="/app/billing">{t('bulkAiPage.planLockedAction')}</Button>
        </InlineMessage>
      )}
      {usageQuery.error && !planLocked && <InlineMessage severity="error">{(usageQuery.error as Error).message}</InlineMessage>}

      {usage && (
        <SectionCard title={t('bulkAiPage.budgetTitle')} subtitle={t('bulkAiPage.budgetSubtitle', { month: usage.month.slice(0, 4) + '-' + usage.month.slice(4) })} sx={{ mb: 3 }}>
          <UsageBar
            label={t('bulkAiPage.budgetLabel', { used: usage.used.toLocaleString(), limit: limitText })}
            used={usage.used}
            limit={usage.limit < 0 ? null : usage.limit}
            unit={t('bulkAiPage.calls')}
          />
        </SectionCard>
      )}

      <TabShell<Tab>
        value={tab}
        onChange={(next) => setTab(next)}
        sticky={false}
        tabs={[
          { key: 'new', label: t('bulkAiPage.tabNew') },
          { key: 'jobs', label: t('bulkAiPage.tabJobs') },
        ]}
      >
        {tab === 'new' ? (
          <NewJobTab
            canWrite={canWrite && !planLocked}
            usage={usage ?? null}
            onRun={(job) => {
              queryClient.invalidateQueries({ queryKey: ['ai-bulk-jobs'] });
              queryClient.invalidateQueries({ queryKey: ['ai-bulk-usage'] });
              selectJob(job.id);
            }}
          />
        ) : selectedJobId ? (
          <JobReview jobId={selectedJobId} canWrite={canWrite && !planLocked} onBack={() => selectJob(null)} />
        ) : (
          <JobsList onSelect={(id) => selectJob(id)} formatDate={(d) => formatDate(d)} formatCurrency={formatCurrency} />
        )}
      </TabShell>
    </PageContainer>
  );
};

/* ------------------------------- New job -------------------------------- */

const NewJobTab: React.FC<{ canWrite: boolean; usage: AiBulkUsage | null; onRun: (job: AiBulkJob) => void }> = ({ canWrite, usage, onRun }) => {
  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  const { showToast } = useToast();
  const [operation, setOperation] = useState<AiBulkOperation>('rewrite_title');
  const [voiceRules, setVoiceRules] = useState('');
  const [brand, setBrand] = useState('');
  const [productType, setProductType] = useState<ProductTypeFilter>('');
  const [missingField, setMissingField] = useState<'' | AiBulkMissingField>('');
  const [search, setSearch] = useState('');
  const [estimated, setEstimated] = useState<AiBulkJob | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const [running, setRunning] = useState<AiBulkJob | null>(null);

  const brandsQuery = useQuery<string[]>({
    queryKey: ['store-brand-names'],
    queryFn: async () => {
      const res = await makeApiCall('/store/product-count', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      const body = await res.json();
      return Array.isArray(body?.data?.brands) ? body.data.brands : [];
    },
  });

  const filter = useMemo(
    () => ({
      ...(brand ? { brands: [brand] } : {}),
      ...(productType ? { productTypes: [productType] } : {}),
      ...(missingField ? { missingField } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [brand, productType, missingField, search],
  );

  // Any change to the inputs invalidates a shown estimate: it was for other inputs.
  useEffect(() => {
    setEstimated(null);
  }, [operation, voiceRules, filter]);

  const estimate = useMutation({
    mutationFn: () =>
      aiBulkApi.createJob({ operation, filter, parameters: { voiceRules: COPY_OPERATIONS.has(operation) && voiceRules.trim() ? voiceRules.trim() : null } }),
    onSuccess: (job) => setEstimated(job),
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const run = useMutation({
    mutationFn: async (jobId: string) => {
      let job = await aiBulkApi.run(jobId);
      setRunning(job);
      // One bounded batch per call; keep going while the server says more rows are pending.
      while (job.status === 'estimated' && job.progress.pending > 0) {
        job = await aiBulkApi.run(jobId);
        setRunning(job);
      }
      return job;
    },
    onSuccess: (job) => {
      setRunning(null);
      if (job.status === 'budget_exhausted') showToast(job.stopReason || t('bulkAiPage.budgetStopped'), 'warning');
      else showToast(t('bulkAiPage.runDone', { count: job.progress.done }), 'success');
      onRun(job);
    },
    onError: (error: Error) => {
      setRunning(null);
      showToast(error.message, 'error');
    },
  });

  const remaining = usage?.remaining ?? null;
  const overBudget = estimated !== null && remaining !== null && estimated.estimate.calls > remaining;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <SectionCard title={t('bulkAiPage.operationTitle')} subtitle={t('bulkAiPage.operationSubtitle')} sx={{ flex: '2 1 560px' }}>
          <ToggleButtonGroup
            exclusive
            orientation="vertical"
            fullWidth
            value={operation}
            onChange={(_e, next: AiBulkOperation | null) => next && setOperation(next)}
            aria-label={t('bulkAiPage.operationTitle')}
          >
            {OPERATIONS.map((op) => (
              <ToggleButton key={op} value={op} sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}>
                <Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t(`bulkAiPage.operations.${op}.label`)}</Typography>
                  <Typography variant="caption" color="text.secondary">{t(`bulkAiPage.operations.${op}.hint`)}</Typography>
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {COPY_OPERATIONS.has(operation) && (
            <TextField
              sx={{ mt: 2 }}
              fullWidth
              multiline
              minRows={2}
              label={t('bulkAiPage.voiceRules')}
              helperText={t('bulkAiPage.voiceRulesHint')}
              value={voiceRules}
              onChange={(e) => setVoiceRules(e.target.value.slice(0, 1000))}
            />
          )}
        </SectionCard>

        <SectionCard title={t('bulkAiPage.subsetTitle')} subtitle={t('bulkAiPage.subsetSubtitle', { max: usage?.maxProducts ?? 500 })} sx={{ flex: '1 1 300px' }}>
          <Stack spacing={2}>
            <TextField select size="small" label={t('bulkAiPage.brand')} value={brand} onChange={(e) => setBrand(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('bulkAiPage.allBrands')}</MenuItem>
              {(brandsQuery.data ?? []).map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('bulkAiPage.productType')} value={productType} onChange={(e) => setProductType(e.target.value as ProductTypeFilter)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('bulkAiPage.allTypes')}</MenuItem>
              <MenuItem value="camera">{t('bulkAiPage.types.camera')}</MenuItem>
              <MenuItem value="lens">{t('bulkAiPage.types.lens')}</MenuItem>
              <MenuItem value="accessory">{t('bulkAiPage.types.accessory')}</MenuItem>
            </TextField>
            <TextField select size="small" label={t('bulkAiPage.missingField')} value={missingField} onChange={(e) => setMissingField(e.target.value as '' | AiBulkMissingField)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('bulkAiPage.anyProduct')}</MenuItem>
              {MISSING_FIELDS.map((f) => (
                <MenuItem key={f} value={f}>{t(`bulkAiPage.missing.${f}`)}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label={t('bulkAiPage.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Tooltip title={canWrite ? '' : t('bulkAiPage.readOnly')}>
              <span>
                <Button variant="outlined" fullWidth disabled={!canWrite || estimate.isPending} onClick={() => estimate.mutate()}>
                  {estimate.isPending ? t('bulkAiPage.estimating') : t('bulkAiPage.estimate')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </SectionCard>
      </Box>

      {estimated && (
        <SectionCard title={t('bulkAiPage.estimateTitle')} subtitle={t('bulkAiPage.estimateSubtitle')}>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {t('bulkAiPage.estimateLine', {
                products: estimated.productCount.toLocaleString(),
                calls: estimated.estimate.calls.toLocaleString(),
                cached: estimated.estimate.cachedRows.toLocaleString(),
                cost: formatCurrency(estimated.estimate.approxCostUsd, 'USD'),
              })}
            </Typography>
            {estimated.truncated && <InlineMessage severity="info">{t('bulkAiPage.truncated', { max: usage?.maxProducts ?? 500 })}</InlineMessage>}
            {overBudget && (
              <InlineMessage severity="warning">
                {t('bulkAiPage.overBudget', { calls: estimated.estimate.calls.toLocaleString(), remaining: (remaining ?? 0).toLocaleString() })}
              </InlineMessage>
            )}
            <Box>
              <Button variant="contained" startIcon={<PlayArrowOutlined />} disabled={!canWrite || run.isPending} onClick={() => setConfirmRun(true)}>
                {running ? t('bulkAiPage.runningProgress', { done: running.progress.done + running.progress.error + running.progress.skipped, total: running.progress.total }) : t('bulkAiPage.run')}
              </Button>
            </Box>
          </Stack>
          <ConfirmDialog
            open={confirmRun}
            title={t('bulkAiPage.confirmRunTitle', { calls: estimated.estimate.calls.toLocaleString() })}
            message={t('bulkAiPage.confirmRunBody')}
            preview={
              <Typography variant="body2">
                {t('bulkAiPage.estimateLine', {
                  products: estimated.productCount.toLocaleString(),
                  calls: estimated.estimate.calls.toLocaleString(),
                  cached: estimated.estimate.cachedRows.toLocaleString(),
                  cost: formatCurrency(estimated.estimate.approxCostUsd, 'USD'),
                })}
              </Typography>
            }
            confirmLabel={t('bulkAiPage.run')}
            cancelLabel={t('common.cancel')}
            loading={run.isPending}
            onConfirm={() => {
              setConfirmRun(false);
              run.mutate(estimated.id);
            }}
            onClose={() => setConfirmRun(false)}
          />
        </SectionCard>
      )}
    </Stack>
  );
};

/* ------------------------------- Jobs list ------------------------------ */

const JobsList: React.FC<{ onSelect: (id: string) => void; formatDate: (d: string) => string; formatCurrency: (n: number, c: 'USD') => string }> = ({ onSelect, formatDate, formatCurrency }) => {
  const { t } = useAppTranslation();
  const jobs = useQuery<AiBulkJob[]>({ queryKey: ['ai-bulk-jobs'], queryFn: aiBulkApi.listJobs });
  if (jobs.isLoading) return <TableSkeleton rows={4} columns={6} />;
  if (jobs.error) return <InlineMessage severity="error">{(jobs.error as Error).message}</InlineMessage>;
  if (!jobs.data?.length) return <EmptyState title={t('bulkAiPage.noJobsTitle')} description={t('bulkAiPage.noJobsBody')} />;
  return (
    <DataTableShell>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('bulkAiPage.colCreated')}</TableCell>
            <TableCell>{t('bulkAiPage.colOperation')}</TableCell>
            <TableCell>{t('bulkAiPage.colStatus')}</TableCell>
            <TableCell align="right">{t('bulkAiPage.colProducts')}</TableCell>
            <TableCell>{t('bulkAiPage.colDecisions')}</TableCell>
            <TableCell align="right">{t('bulkAiPage.colSpent')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.data.map((job) => (
            <TableRow key={job.id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelect(job.id)}>
              <TableCell>{job.createdAt ? formatDate(job.createdAt) : ''}</TableCell>
              <TableCell>{t(`bulkAiPage.operations.${job.operation}.label`)}</TableCell>
              <TableCell><StatusChip dot tone={JOB_TONE[job.status]} label={t(`bulkAiPage.status.${job.status}`)} /></TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{job.productCount}</TableCell>
              <TableCell>{t('bulkAiPage.decisionsLine', { approved: job.decisions.approved, rejected: job.decisions.rejected, pending: job.decisions.pending, applied: job.appliedCount })}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{t('bulkAiPage.spentLine', { calls: job.spent.calls, cost: formatCurrency(job.spent.approxCostUsd, 'USD') })}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DataTableShell>
  );
};

/* ------------------------------- Job review ----------------------------- */

const JobReview: React.FC<{ jobId: string; canWrite: boolean; onBack: () => void }> = ({ jobId, canWrite, onBack }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<RowFilter>('all');
  const [confirmApply, setConfirmApply] = useState(false);

  const jobQuery = useQuery<AiBulkJob>({ queryKey: ['ai-bulk-job', jobId], queryFn: () => aiBulkApi.getJob(jobId) });
  const rowsQuery = useQuery<AiBulkRow[]>({ queryKey: ['ai-bulk-rows', jobId], queryFn: () => aiBulkApi.listRows(jobId) });
  const job = jobQuery.data;
  const rows = rowsQuery.data ?? [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ai-bulk-job', jobId] });
    queryClient.invalidateQueries({ queryKey: ['ai-bulk-rows', jobId] });
    queryClient.invalidateQueries({ queryKey: ['ai-bulk-jobs'] });
    queryClient.invalidateQueries({ queryKey: ['ai-bulk-usage'] });
  };

  const decide = useMutation({
    mutationFn: (body: { rowIds?: string[]; all?: true; decision: AiBulkRowDecision }) => aiBulkApi.decide(jobId, body),
    onSuccess: refresh,
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const apply = useMutation({
    mutationFn: () => aiBulkApi.apply(jobId),
    onSuccess: (result) => {
      refresh();
      if (result.failed > 0) showToast(t('bulkAiPage.appliedPartial', { applied: result.applied, failed: result.failed }), 'warning');
      else showToast(t('bulkAiPage.applied', { applied: result.applied }), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const resume = useMutation({
    mutationFn: async () => {
      let next = await aiBulkApi.run(jobId);
      while (next.status === 'estimated' && next.progress.pending > 0) next = await aiBulkApi.run(jobId);
      return next;
    },
    onSuccess: (next) => {
      refresh();
      if (next.status === 'budget_exhausted') showToast(next.stopReason || t('bulkAiPage.budgetStopped'), 'warning');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const counts = useMemo(() => {
    const c: Record<RowFilter, number> = { all: rows.length, pending: 0, approved: 0, rejected: 0, error: 0 };
    for (const row of rows) {
      if (row.status === 'error' || row.status === 'skipped') c.error += 1;
      else if (row.status === 'done') c[row.decision] += 1;
    }
    return c;
  }, [rows]);
  const visible = rows.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'error') return row.status === 'error' || row.status === 'skipped';
    return row.status === 'done' && row.decision === filter;
  });
  const approvedToApply = rows.filter((r) => r.status === 'done' && r.decision === 'approved' && !r.applied).length;
  const canResume = job && (job.status === 'estimated' || job.status === 'budget_exhausted') && job.progress.pending > 0;

  if (jobQuery.error) return <InlineMessage severity="error">{(jobQuery.error as Error).message}</InlineMessage>;

  return (
    <Stack spacing={2}>
      <SectionCard
        title={job ? t(`bulkAiPage.operations.${job.operation}.label`) : t('bulkAiPage.loading')}
        subtitle={job ? t('bulkAiPage.coverage', { approved: counts.approved, total: job.productCount, rejected: counts.rejected, pending: counts.pending, applied: job.appliedCount }) : undefined}
        actions={
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onBack}>{t('bulkAiPage.backToJobs')}</Button>
            {canResume && (
              <Button size="small" variant="outlined" startIcon={<PlayArrowOutlined />} disabled={!canWrite || resume.isPending} onClick={() => resume.mutate()}>
                {t('bulkAiPage.resume', { pending: job!.progress.pending })}
              </Button>
            )}
          </Stack>
        }
      >
        <Stack spacing={1}>
          {job && <StatusChip dot tone={JOB_TONE[job.status]} label={t(`bulkAiPage.status.${job.status}`)} />}
          {job?.stopReason && <InlineMessage severity="warning">{job.stopReason}</InlineMessage>}
          {job?.parameters?.voiceRules && (
            <Typography variant="body2" color="text.secondary">{t('bulkAiPage.voiceRules')}: {job.parameters.voiceRules}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">{t('bulkAiPage.nothingUntilApply')}</Typography>
        </Stack>
      </SectionCard>

      <DataTableShell
        toolbar={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
            <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_e, next: RowFilter | null) => next && setFilter(next)} aria-label={t('bulkAiPage.rowFilter')}>
              {(['all', 'pending', 'approved', 'rejected', 'error'] as RowFilter[]).map((key) => (
                <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
                  {t(`bulkAiPage.filters.${key}`)}
                  <Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums', color: key === 'pending' && counts.pending > 0 ? tokens.signal.caution.ink : 'inherit' }}>
                    {counts[key]}
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<CheckOutlined />} disabled={!canWrite || decide.isPending || counts.pending + counts.rejected === 0} onClick={() => decide.mutate({ all: true, decision: 'approved' })}>
                {t('bulkAiPage.approveAll')}
              </Button>
              <Button size="small" startIcon={<CloseOutlined />} disabled={!canWrite || decide.isPending || counts.pending + counts.approved === 0} onClick={() => decide.mutate({ all: true, decision: 'rejected' })}>
                {t('bulkAiPage.rejectAll')}
              </Button>
              <Tooltip title={!canWrite ? t('bulkAiPage.readOnly') : approvedToApply === 0 ? t('bulkAiPage.nothingToApply') : ''}>
                <span>
                  <Button size="small" variant="contained" startIcon={<AutoFixHighOutlined />} disabled={!canWrite || approvedToApply === 0 || apply.isPending} onClick={() => setConfirmApply(true)}>
                    {t('bulkAiPage.applyApproved', { count: approvedToApply })}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        }
      >
        {rowsQuery.isLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : visible.length === 0 ? (
          <EmptyState size="compact" title={t(`bulkAiPage.empty.${filter}`)} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('bulkAiPage.colProduct')}</TableCell>
                <TableCell sx={{ width: '30%' }}>{t('bulkAiPage.colBefore')}</TableCell>
                <TableCell sx={{ width: '30%' }}>{t('bulkAiPage.colAfter')}</TableCell>
                <TableCell>{t('bulkAiPage.colDecision')}</TableCell>
                <TableCell align="right">{t('bulkAiPage.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => {
                const decidable = canWrite && row.status === 'done' && !row.applied && !decide.isPending;
                return (
                  <TableRow key={row.id} sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.productName}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: tokens.font.mono }}>{row.sku || row.productId}</Typography>
                      {row.cached && <Box><StatusChip size="small" tone="neutral" label={t('bulkAiPage.cached')} /></Box>}
                    </TableCell>
                    <TableCell>{renderValue(row.before)}</TableCell>
                    <TableCell>
                      {row.status === 'done' ? renderValue(row.after) : row.status === 'pending' ? (
                        <Typography variant="body2" color="text.secondary">{t('bulkAiPage.notGenerated')}</Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: tokens.signal.problem.ink }}>{row.error || t('bulkAiPage.rowFailed')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.applied ? (
                        <StatusChip dot tone="positive" label={t('bulkAiPage.rowApplied')} />
                      ) : row.status === 'done' ? (
                        <StatusChip dot tone={DECISION_TONE[row.decision]} label={t(`bulkAiPage.decision.${row.decision}`)} />
                      ) : (
                        <StatusChip dot tone={row.status === 'pending' ? 'neutral' : 'problem'} label={t(`bulkAiPage.rowStatus.${row.status}`)} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                        <Button size="small" variant={row.decision === 'approved' ? 'contained' : 'outlined'} disabled={!decidable || row.decision === 'approved'} onClick={() => decide.mutate({ rowIds: [row.id], decision: 'approved' })}>
                          {t('bulkAiPage.approve')}
                        </Button>
                        <Button size="small" color="inherit" variant={row.decision === 'rejected' ? 'contained' : 'outlined'} disabled={!decidable || row.decision === 'rejected'} onClick={() => decide.mutate({ rowIds: [row.id], decision: 'rejected' })}>
                          {t('bulkAiPage.reject')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataTableShell>

      <ConfirmDialog
        open={confirmApply}
        title={t('bulkAiPage.confirmApplyTitle', { count: approvedToApply })}
        message={t('bulkAiPage.confirmApplyBody', { field: job ? t(`bulkAiPage.targets.${job.target}`) : '' })}
        confirmLabel={t('bulkAiPage.applyApproved', { count: approvedToApply })}
        cancelLabel={t('common.cancel')}
        loading={apply.isPending}
        onConfirm={() => {
          setConfirmApply(false);
          apply.mutate();
        }}
        onClose={() => setConfirmApply(false)}
      />
    </Stack>
  );
};

export default StoreBulkAiPage;
